// ABOUTME: The condition builder of the bulk change scope: operators per field type, their value editors and labels.
// ABOUTME: Converts between a condition row and the list view's SearchFilter, keeping filters it cannot edit as read-only.

import { CtgStatus, SearchFilter, SearchFilterOperator } from '@src/__generated__/types';
import { defineMessages, MessageDescriptor } from 'react-intl';

/** One row of the condition builder; `operator` is empty until the user picks one. */
export type ConditionRow = {
    field: string;
    operator: SearchFilterOperator | '';
    values: string[];
    language?: string;
};

/** The system fields the builder offers, besides the content types. */
export const CODE_FIELD = 'code';
export const LIST_NAME_FIELD = 'listName';
export const STATUS_FIELD = 'nodePublished';
export const SYSTEM_CONDITION_FIELDS = [CODE_FIELD, LIST_NAME_FIELD, STATUS_FIELD];

/** Pseudo types of the system fields. */
const SYSTEM_TEXT_TYPE = 'systemText';
const STATUS_TYPE = 'status';

/** What the builder needs to know about a field. */
export type ConditionFieldInfo = { type: string; multiLanguage: boolean };

export type ConditionValueEditor =
    | 'none'
    | 'text'
    | 'number'
    | 'date'
    | 'boolean'
    | 'options'
    | 'nodes';

/** The search key of a content type: `<ctg>-<ct>`, as the list view filters it. */
export const contentFieldKey = (ctg: string, ct: string) => `${ctg}-${ct}`;

export const STATUS_VALUES: string[] = [
    CtgStatus.Draft,
    CtgStatus.Approved,
    CtgStatus.Published,
    CtgStatus.PublishedDraft,
];

const {
    Contains,
    Equals,
    NotEquals,
    StartsWith,
    EndsWith,
    IsEmpty,
    IsNotEmpty,
    LessThan,
    LessThanEqual,
    GreaterThan,
    GreaterThanEqual,
    IsAnyOf,
} = SearchFilterOperator;

const EMPTINESS = [IsEmpty, IsNotEmpty];
const COMPARISONS = [LessThan, LessThanEqual, GreaterThan, GreaterThanEqual];

/** The operators the Condition select offers for a field type (or a system pseudo type). */
export function operatorsForType(type: string): SearchFilterOperator[] {
    switch (type) {
        case SYSTEM_TEXT_TYPE:
            return [Contains, Equals, StartsWith, EndsWith];
        case STATUS_TYPE:
            return [IsAnyOf];
        case 'text':
        case 'html':
            return [Contains, Equals, NotEquals, StartsWith, EndsWith, ...EMPTINESS];
        case 'number':
        case 'measure':
        case 'currency':
            return [Equals, NotEquals, ...COMPARISONS, ...EMPTINESS];
        case 'dateRange':
            return [...COMPARISONS, ...EMPTINESS];
        case 'select':
        case 'link':
        case 'media':
            return [IsAnyOf, ...EMPTINESS];
        case 'boolean':
            return [Equals];
        default:
            return EMPTINESS;
    }
}

/** The field info of a condition field: a system field, or a content type of the options. */
export function conditionFieldInfo(
    field: string,
    contentTypes: { ctg: string; ct: string; type: string; multiLanguage: boolean }[],
): ConditionFieldInfo | null {
    if (field === CODE_FIELD || field === LIST_NAME_FIELD) {
        return { type: SYSTEM_TEXT_TYPE, multiLanguage: false };
    }
    if (field === STATUS_FIELD) return { type: STATUS_TYPE, multiLanguage: false };
    const contentType = contentTypes.find(c => contentFieldKey(c.ctg, c.ct) === field);
    return contentType
        ? { type: contentType.type, multiLanguage: contentType.multiLanguage }
        : null;
}

/**
 * The value input a condition needs: none for an emptiness test. The search indexes a content in
 * the default language only, as the list view filters it, so an emptiness test takes no language.
 */
export function valueEditorFor(
    info: ConditionFieldInfo | null,
    operator: SearchFilterOperator | '',
): ConditionValueEditor {
    if (!info || !operator) return 'none';
    if (EMPTINESS.includes(operator)) return 'none';
    if (operator === IsAnyOf) {
        if (info.type === 'link' || info.type === 'media') return 'nodes';
        return 'options';
    }
    switch (info.type) {
        case 'boolean':
            return 'boolean';
        case 'number':
        case 'measure':
        case 'currency':
            return 'number';
        case 'dateRange':
            return 'date';
        default:
            return 'text';
    }
}

/** The filter of a complete row; null while the row misses its field, operator or value. */
export function toSearchFilter(row: ConditionRow): SearchFilter | null {
    if (!row.field || !row.operator) return null;
    const emptiness = EMPTINESS.includes(row.operator);
    const values = row.values.filter(value => value !== '');
    if (!emptiness && !values.length) return null;
    return {
        field: row.field,
        operator: row.operator,
        value: emptiness ? [] : values,
        ...(row.language ? { language: row.language } : {}),
    };
}

const OPERATORS = Object.values(SearchFilterOperator) as string[];
const SYSTEM_PREFIX = 'system-';

/**
 * The row of a filter, or null when the builder cannot edit it (a linked-by, depth or ancestor
 * filter carried over from the list): the caller keeps such a filter as a read-only chip.
 */
export function fromSearchFilter(filter: SearchFilter): ConditionRow | null {
    if (filter.linkedHierarchy || !OPERATORS.includes(filter.operator)) return null;
    const field = filter.field.startsWith(SYSTEM_PREFIX)
        ? filter.field.slice(SYSTEM_PREFIX.length)
        : filter.field;
    const isSystem = SYSTEM_CONDITION_FIELDS.includes(field);
    const isContent = !filter.field.startsWith(SYSTEM_PREFIX) && /^[^-]+-.+$/.test(field);
    if (!isSystem && !isContent) return null;
    if (isSystem) {
        const info = conditionFieldInfo(field, []);
        if (!info || !operatorsForType(info.type).includes(filter.operator)) return null;
    }
    return {
        field,
        operator: filter.operator,
        values: [...filter.value],
        ...(filter.language ? { language: filter.language } : {}),
    };
}

export const operatorMessages: Record<SearchFilterOperator, MessageDescriptor> = defineMessages({
    [Contains]: { id: 'bulkChange.operator.contains', defaultMessage: 'contains' },
    [Equals]: { id: 'bulkChange.operator.equals', defaultMessage: 'is' },
    [NotEquals]: { id: 'bulkChange.operator.notEquals', defaultMessage: 'is not' },
    [StartsWith]: { id: 'bulkChange.operator.startsWith', defaultMessage: 'starts with' },
    [EndsWith]: { id: 'bulkChange.operator.endsWith', defaultMessage: 'ends with' },
    [IsEmpty]: { id: 'bulkChange.operator.isEmpty', defaultMessage: 'is empty' },
    [IsNotEmpty]: { id: 'bulkChange.operator.isNotEmpty', defaultMessage: 'is not empty' },
    [LessThan]: { id: 'bulkChange.operator.lessThan', defaultMessage: 'is less than (<)' },
    [LessThanEqual]: { id: 'bulkChange.operator.lessThanEqual', defaultMessage: 'is at most (≤)' },
    [GreaterThan]: { id: 'bulkChange.operator.greaterThan', defaultMessage: 'is more than (>)' },
    [GreaterThanEqual]: {
        id: 'bulkChange.operator.greaterThanEqual',
        defaultMessage: 'is at least (≥)',
    },
    [IsAnyOf]: { id: 'bulkChange.operator.isAnyOf', defaultMessage: 'is any of' },
    [SearchFilterOperator.ExactMatch]: {
        id: 'bulkChange.operator.exactMatch',
        defaultMessage: 'matches exactly',
    },
    [SearchFilterOperator.Includes]: {
        id: 'bulkChange.operator.includes',
        defaultMessage: 'includes',
    },
});

export const statusMessages: Record<string, MessageDescriptor> = defineMessages({
    [CtgStatus.Draft]: { id: 'bulkChange.status.draft', defaultMessage: 'Draft' },
    [CtgStatus.Approved]: { id: 'bulkChange.status.approved', defaultMessage: 'Approved' },
    [CtgStatus.Published]: { id: 'bulkChange.status.published', defaultMessage: 'Published' },
    [CtgStatus.PublishedDraft]: {
        id: 'bulkChange.status.publishedDraft',
        defaultMessage: 'Published, with draft changes',
    },
});
